import { Skeleton, Typography } from '@mui/joy';
import {
  FetchBaseQueryError,
  TypedUseQueryHookResult,
} from '@reduxjs/toolkit/query/react';
import React from 'react';

import { Button } from '~/components/ui/button';
import { useTypedSelector } from '~/utils/store';

const DefaultLoadingElement = () => {
  return (
    <div className='relative'>
      <Skeleton level='body-sm' variant='text' width='inherit' />
      <Skeleton level='body-sm' variant='text' width='inherit' />
      <Skeleton level='body-sm' variant='text' width='inherit' />
    </div>
  );
};

type ResolveStrategy = 'default' | 'stale-while-revalidate';
const STRATEGY_CONDITIONS: Record<
  ResolveStrategy,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <Query extends TypedUseQueryHookResult<unknown, unknown, any>>(
    q: Query,
  ) => boolean
> = {
  default: (q) => !q.isFetching,
  'stale-while-revalidate': () => true,
};
export default function QueryResolver<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends TypedUseQueryHookResult<unknown, unknown, any>,
  TData = T['data'],
>({
  children,
  strategy = 'default',
  query,
  onRefetch: onRefetchHandler = query.refetch,
  loadingElement = <DefaultLoadingElement />,
  errorElement = null,
  uninitializedElement = null,
}: {
  query: T;
  strategy?: ResolveStrategy;
  children: ((d: NonNullable<TData>) => React.ReactNode) | React.ReactNode;
  onRefetch?: () => void;
  loadingElement?: React.ReactNode;
  errorElement?:
    | ((error: FetchBaseQueryError) => React.ReactNode)
    | React.ReactNode;
  uninitializedElement?: React.ReactNode;
}) {
  const resolveCondition = STRATEGY_CONDITIONS[strategy](query);

  if (query.isUninitialized) {
    return <>{uninitializedElement}</>;
  }

  if (query.isError) {
    if (errorElement !== null) {
      return (
        <>
          {typeof errorElement === 'function'
            ? errorElement(query.error)
            : errorElement}
        </>
      );
    }

    return (
      <div className='flex items-center justify-center px-4 py-16'>
        <div>
          <div>
            <Typography level='title-lg'>Error</Typography>
            <Typography>Failed to load data. Try again.</Typography>
          </div>
          <div className='mt-2'>
            <Button
              color='neutral'
              variant='soft'
              size='md'
              onClick={onRefetchHandler}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /**
   *
   * In the future we can consolidate this behavior to a prop to
   * choose if we want to show the cached data while fetching a new one
   * or show the loading element when the query is fetching.
   */
  if (query.isSuccess && resolveCondition && query.data !== undefined) {
    return (
      <>
        {typeof children === 'function'
          ? children(query.data as NonNullable<TData>)
          : children}
      </>
    );
  }

  return <>{loadingElement}</>;
}

export function QueryRevalidating({ children }: { children: React.ReactNode }) {
  const isSomeQueryFetching = useTypedSelector((state) =>
    Object.values(state.api.queries).some(
      (query) => query?.status === 'pending',
    ),
  );
  if (isSomeQueryFetching) {
    return <>{children}</>;
  }
  return null;
}
